import React, { FC, useState } from 'react';
import { Instance } from 'mobx-state-tree';

/* Types */
import { Actions } from '~/src/components/RowActions/RowActions';
import { PdfCourtFormStore } from '~/src/stores/pdfCourtFormsStore';
import { stateCodeToStateName } from '~/src/utils/stateCodeToStateName';
/* Components */
import CourtFormsLandingWrapper from './CourtFormsLandingWrapper';
import StateRegionTableWrapper from './StateRegionTableWrapper';
import PdfTableWrapper from './PdfTableWrapper';
import RegionalCourtFormsWrapper from './RegionalCourtFormsWrapper';
import FederalFormsWithLocalitiesContainer, {
  GENERAL_FEDERAL_FORMS_CATEGORY,
} from '~/src/containers/FederalFormsWithLocalitiesContainer';
import type { BreadcrumbDataItem } from '../Breadcrumb/Breadcrumb';

import { useSubscription } from '~/src/entities/subscription';
import {
  getAdditionalRegionForms,
  AdditionalRegionInfo,
} from '~/src/utils/additionalRegionForms';
import useGetTemplateMetadata from '~/src/hooks/useGetTemplateMetadata';
import useFeatureAccesses from '~/src/hooks/useFeatureAccesses';

interface PdfCourtForm extends Instance<typeof PdfCourtFormStore> {}

interface Props {
  actionType: string;
  disableAddButton: boolean;
  rowActions?: Actions<PdfCourtForm>;
  handleAddTemplateToSeletionSidebar: (t: PdfCourtForm) => void;
  handlePreviewTemplate: (t: PdfCourtForm) => void;
  containedInSidebar: (t: PdfCourtForm) => boolean;
}

const CourtFormsWrapper: FC<Props> = ({
  actionType,
  disableAddButton,
  rowActions,
  handleAddTemplateToSeletionSidebar,
  handlePreviewTemplate,
  containedInSidebar,
}) => {
  const { data: subscriptionData } = useSubscription();
  const additionalRegions = getAdditionalRegionForms(
    subscriptionData?.additional_court_form_regions,
  );
  const { data: templateMetadata } = useGetTemplateMetadata();
  const { isFederalFormsCategoriesEnabled } = useFeatureAccesses();
  const federalFormsLabel = isFederalFormsCategoriesEnabled
    ? 'Federal forms'
    : 'USCIS forms';

  const [stateCode, setStateCode] = useState<
    keyof typeof stateCodeToStateName | ''
  >('');
  const [locality, setLocality] = useState('');
  const [tableType, setTableType] = useState('');
  const [showFederalCategories, setShowFederalCategories] = useState(false);
  const [selectedAdditionalRegion, setSelectedAdditionalRegion] =
    useState<AdditionalRegionInfo | null>(null);
  const [selectedFilterCategory, setSelectedFilterCategory] = useState('');

  const handleStateFormsSelect = (
    stateCode: keyof typeof stateCodeToStateName,
  ) => {
    setTableType('state');
    setStateCode(stateCode);
    if (stateCode === 'FEDERAL' && !isFederalFormsCategoriesEnabled) {
      setLocality('');
    } else {
      setLocality('None');
    }
  };

  const handleStateReginalFormsSelect = (
    stateCode: keyof typeof stateCodeToStateName,
  ) => {
    setTableType('region');
    setStateCode(stateCode);
  };

  const handleLocalitySelect = (locality: string) => {
    setTableType('state');
    setLocality(locality);
  };

  const handleFederalCategorySelect = (categoryLocality: string) => {
    setShowFederalCategories(false);
    setStateCode('FEDERAL');
    setTableType('state');
    if (categoryLocality === GENERAL_FEDERAL_FORMS_CATEGORY) {
      setLocality('None');
    } else {
      setLocality(categoryLocality);
    }
  };

  const handleViewAllFederalCategories = () => {
    setShowFederalCategories(true);
  };

  const handleAdditionalRegionProvinceSelect = (
    region: AdditionalRegionInfo,
    filterCategory: string,
  ) => {
    setSelectedAdditionalRegion(region);
    setSelectedFilterCategory(filterCategory);
    setTableType('additional-region');
  };

  const showCourtFormLanding = !tableType && !showFederalCategories;
  const showStateRegionTable = tableType === 'region';
  const showPDFTable = tableType === 'state';
  const showAdditionalRegionForms = tableType === 'additional-region';

  const resetToLanding = () => {
    setTableType('');
    setStateCode('');
    setLocality('');
    setShowFederalCategories(false);
    setSelectedAdditionalRegion(null);
    setSelectedFilterCategory('');
  };

  const breadcrumbData: BreadcrumbDataItem[] = [
    {
      label: 'Court forms',
      onClick: resetToLanding,
    },
  ];

  if (showFederalCategories) {
    breadcrumbData.push({
      label: federalFormsLabel,
    });
  } else if (stateCode) {
    if (stateCode === 'FEDERAL') {
      if (isFederalFormsCategoriesEnabled) {
        breadcrumbData.push({
          label: federalFormsLabel,
          onClick: () => {
            handleViewAllFederalCategories();
            setTableType('');
            setStateCode('');
            setLocality('');
          },
        });
        if (locality && locality !== 'None') {
          breadcrumbData.push({ label: locality });
        } else if (locality === 'None') {
          breadcrumbData.push({ label: GENERAL_FEDERAL_FORMS_CATEGORY });
        }
      } else {
        breadcrumbData.push({
          label: federalFormsLabel,
        });
      }
    } else {
      const stateName = stateCodeToStateName[stateCode];
      if (tableType === 'region') {
        breadcrumbData.push({
          label: `${stateName} regional forms`,
        });
      } else if (locality && locality !== 'None') {
        breadcrumbData.push({
          label: `${stateName} regional forms`,
          onClick: () => {
            setTableType('region');
            setStateCode(stateCode);
            setLocality('');
          },
        });
        breadcrumbData.push({
          label: `${locality}`,
        });
      } else {
        breadcrumbData.push({
          label: `${stateName} forms`,
        });
      }
    }
  }

  return (
    <div>
      {showFederalCategories && (
        <FederalFormsWithLocalitiesContainer
          actionType="button"
          onCategorySelect={handleFederalCategorySelect}
          breadcrumbData={breadcrumbData}
        />
      )}
      {showCourtFormLanding && (
        <CourtFormsLandingWrapper
          handleStateFormsSelect={handleStateFormsSelect}
          handleStateReginalFormsSelect={handleStateReginalFormsSelect}
          handleFederalCategorySelect={handleFederalCategorySelect}
          handleViewAllFederalCategories={handleViewAllFederalCategories}
          additionalRegions={additionalRegions}
          templateMetadata={templateMetadata}
          onAdditionalRegionProvinceSelect={
            handleAdditionalRegionProvinceSelect
          }
        />
      )}
      {showAdditionalRegionForms && selectedAdditionalRegion && (
        <RegionalCourtFormsWrapper
          regionFilter={selectedAdditionalRegion.region}
          rootLabel={selectedAdditionalRegion.label}
          initialFilterCategory={selectedFilterCategory}
          onBack={resetToLanding}
          disableAddButton={disableAddButton}
          rowActions={rowActions}
          handleAddTemplateToSeletionSidebar={
            handleAddTemplateToSeletionSidebar
          }
          handlePreviewTemplate={handlePreviewTemplate}
          containedInSidebar={containedInSidebar}
        />
      )}
      {showStateRegionTable && (
        <StateRegionTableWrapper
          actionType={actionType}
          stateCode={stateCode}
          breadcrumbData={breadcrumbData}
          handleClick={handleLocalitySelect}
        />
      )}
      {showPDFTable && (
        <PdfTableWrapper
          stateCode={stateCode}
          locality={locality}
          breadcrumbData={breadcrumbData}
          disableAddButton={disableAddButton}
          rowActions={rowActions}
          handleAddTemplateToSeletionSidebar={
            handleAddTemplateToSeletionSidebar
          }
          handlePreviewTemplate={handlePreviewTemplate}
          containedInSidebar={containedInSidebar}
        />
      )}
    </div>
  );
};

export default CourtFormsWrapper;
